import { useState } from 'react';
import { KeyRound, CheckCircle, AlertCircle, X, ArrowLeft, Mail } from 'lucide-react';

interface ForgotPasswordModalProps {
  onClose: () => void;
  onSendResetEmail: (email: string) => Promise<void>;
}

export function ForgotPasswordModal({ onClose, onSendResetEmail }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    setSendError('');

    try {
      await onSendResetEmail(email);
      setSendSuccess(true);
    } catch (error: any) {
      setSendError(error.message || 'Failed to send reset email');
    } finally {
      setIsSending(false);
    }
  };

  const handleResend = async () => {
    setIsSending(true);
    setSendError('');

    try {
      await onSendResetEmail(email);
      // Success already shown
    } catch (error: any) {
      setSendError(error.message || 'Failed to resend reset email');
    } finally {
      setIsSending(false);
    }
  };

  // Success state
  if (sendSuccess) {
    return (
      <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone rounded-4xl shadow-soft-xl max-w-md w-full overflow-hidden">
          <div className="p-8 space-y-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex justify-center mb-6">
                  <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                    <CheckCircle className="w-12 h-12 text-olive" />
                  </div>
                </div>
                <h2 className="text-3xl font-serif font-light tracking-tight text-center mb-2 text-charcoal">
                  Check Your Email
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-sand hover:text-charcoal transition-colors duration-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center space-y-4">
              <p className="text-sand font-light">
                We sent password reset instructions to:
              </p>
              <p className="font-medium text-charcoal bg-alabaster px-5 py-2.5 rounded-full border border-stone inline-block">
                {email}
              </p>
            </div>

            <div className="bg-alabaster border border-stone rounded-3xl p-5 space-y-2">
              <div className="flex items-start gap-3">
                <KeyRound className="w-5 h-5 text-olive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-2 text-charcoal">Next Steps:</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-sand font-light">
                    <li>Open the password reset email</li>
                    <li>Click the reset password link</li>
                    <li>Enter your new password</li>
                    <li>Sign in with your new password!</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="text-sm text-sand text-center font-light">
                Didn't receive the email?
              </div>

              {sendError && (
                <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <p className="text-sm text-terracotta font-light">{sendError}</p>
                </div>
              )}

              <button
                onClick={handleResend}
                disabled={isSending}
                className="w-full bg-olive hover:bg-charcoal text-alabaster py-3.5 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                {isSending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    <span>Resend Reset Email</span>
                  </>
                )}
              </button>
            </div>

            <div className="pt-4 border-t border-stone">
              <div className="text-xs text-sand space-y-1.5 font-light">
                <p className="flex items-center gap-2">
                  <span className="text-olive">•</span>
                  Check your spam folder
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-olive">•</span>
                  The email will arrive within a few minutes
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-olive">•</span>
                  The reset link expires in 1 hour
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Request form
  return (
    <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-stone rounded-4xl shadow-soft-xl max-w-md w-full overflow-hidden">
        <div className="p-8 space-y-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex justify-center mb-6">
                <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                  <KeyRound className="w-12 h-12 text-olive" />
                </div>
              </div>
              <h2 className="text-3xl font-serif font-light tracking-tight text-center mb-2 text-charcoal">
                Reset Your Password
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-sand hover:text-charcoal transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sand text-center font-light">
            Enter your email address and we'll send you instructions to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {sendError && (
              <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                <p className="text-sm text-terracotta font-light">{sendError}</p>
              </div>
            )}

            <div>
              <label htmlFor="reset-email" className="block text-xs font-mono uppercase tracking-widest text-sand mb-3">
                Email Address
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-5 py-3.5 bg-alabaster border border-stone rounded-full text-charcoal placeholder:text-antique focus:outline-none focus:border-olive focus:ring-2 focus:ring-olive/10 transition-all duration-300 font-light"
                placeholder="you@company.com"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={isSending || !email}
              className="w-full bg-olive hover:bg-charcoal text-alabaster py-3.5 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {isSending ? (
                <>
                  <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <KeyRound className="w-5 h-5" />
                  <span>Send Reset Instructions</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-stone">
            <p className="text-sm text-sand text-center font-light">
              Remember your password?{' '}
              <button
                onClick={onClose}
                className="text-charcoal hover:text-terracotta font-medium transition-colors duration-300"
              >
                Back to Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
